import React, { useEffect, useRef, useState } from 'react';
import { Download, Upload, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { recuentosAPI, formatFechaHora, downloadBlob, errorMsg } from '@/lib/recuentos';

const LugarRow = ({ l, onSaved }) => {
  const [local, setLocal] = useState(l.local);
  const [bodega, setBodega] = useState(l.nombre_bodega_justo);
  const changed = local !== l.local || bodega !== l.nombre_bodega_justo;

  const save = async (data) => {
    try {
      await recuentosAPI.updateLugar(l.id, data);
      toast.success('Lugar actualizado');
      onSaved();
    } catch (e) { toast.error(errorMsg(e, 'Error')); }
  };

  return (
    <tr className="border-t" data-testid={`lugar-row-${l.codigo}`}>
      <td className="px-3 py-2 font-mono text-xs text-slate-400">{l.codigo}</td>
      <td className="px-3 py-2"><Input value={local} onChange={(e) => setLocal(e.target.value)} data-testid={`lugar-local-${l.codigo}`} /></td>
      <td className="px-3 py-2"><Input value={bodega} onChange={(e) => setBodega(e.target.value)} data-testid={`lugar-bodega-${l.codigo}`} /></td>
      <td className="px-3 py-2 text-center"><Switch checked={l.activo} onCheckedChange={(c) => save({ activo: c })} data-testid={`lugar-activo-${l.codigo}`} /></td>
      <td className="px-3 py-2">
        <Button size="sm" disabled={!changed} onClick={() => save({ local, nombre_bodega_justo: bodega })} data-testid={`lugar-guardar-${l.codigo}`}>Guardar</Button>
      </td>
    </tr>
  );
};

const PlantillaCard = () => {
  const [info, setInfo] = useState(null);
  const fileRef = useRef(null);
  const load = () => recuentosAPI.plantillaInfo().then((r) => setInfo(r.data));
  useEffect(() => { load(); }, []);

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await recuentosAPI.subirPlantilla(file);
      toast.success('Plantilla reemplazada');
      load();
    } catch (err) { toast.error(errorMsg(err, 'Error al subir plantilla')); }
  };

  const descargar = async () => {
    const res = await recuentosAPI.descargarPlantilla();
    downloadBlob(res.data, 'plantilla_justo.xlsx');
  };

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="plantilla-card">
      <h3 className="font-semibold text-slate-800 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-green-600" /> Plantilla base de Justo</h3>
      {info?.existe ? (
        <div className="text-sm text-slate-600 space-y-1" data-testid="plantilla-info">
          <p>Archivo: {info.filename}{info.actualizada ? ` · reemplazada ${formatFechaHora(info.actualizada)}` : ''}</p>
          <p>Hojas: {info.hojas.join(', ')}</p>
        </div>
      ) : <p className="text-sm text-amber-700">No hay plantilla. Se generará un archivo con la hoja "Ingredientes".</p>}
      <p className="text-xs text-slate-500">Al exportar se llena solo la hoja "Ingredientes" (ID Ingrediente, Nombre Ingrediente, Cantidad) y el resto de hojas queda igual.</p>
      <div className="flex gap-2">
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={subir} data-testid="plantilla-subir-input" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="plantilla-subir-btn"><Upload className="w-4 h-4 mr-1" /> Reemplazar plantilla</Button>
        {info?.existe && <Button variant="outline" onClick={descargar} data-testid="plantilla-descargar-btn"><Download className="w-4 h-4 mr-1" /> Descargar</Button>}
      </div>
    </div>
  );
};

export const ConfigTab = ({ lugares, onLugaresChange }) => (
  <div className="space-y-6 mt-4">
    <div className="bg-white rounded-xl border overflow-x-auto">
      <div className="px-4 pt-4 font-semibold text-slate-800">Lugares de stock</div>
      <table className="w-full text-sm mt-2" data-testid="lugares-table">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left">Código</th>
            <th className="px-3 py-2 text-left">Local</th>
            <th className="px-3 py-2 text-left">Bodega en Justo</th>
            <th className="px-3 py-2 text-center">Activo</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>{lugares.map((l) => <LugarRow key={l.id} l={l} onSaved={onLugaresChange} />)}</tbody>
      </table>
    </div>
    <PlantillaCard />
  </div>
);
